import { useOnboarding, PRICING } from "./OnboardingContext";
import { Package, Check, ArrowLeft, ArrowRight, Droplets, Calendar, Settings, TrendingUp, Image, Clock, Info } from "lucide-react";

const StepSummary = () => {
  const {
    selectedPackage,
    selectedDistributionTier,
    numberOfMachines,
    selectedAddOns,
    logoFile,
    monthlyPrice,
    setupFee,
    nextStep,
    prevStep,
    getPackageDetails,
    getDistributionTierDetails
  } = useOnboarding();

  const pkg = getPackageDetails();
  const tier = getDistributionTierDetails();
  if (!pkg) return null;

  const additionalMachines = Math.max(0, numberOfMachines - 1);
  const tierPrice = tier?.price || 0;
  const machinesCost = additionalMachines * pkg.basePrice;
  const addOnsCost = selectedAddOns.reduce((sum, id) => sum + (PRICING.addOns[id]?.price || 0), 0);

  // Check if accelerated deployment is selected
  const hasAcceleratedDeployment = selectedAddOns.includes("acceleratedDeployment");

  return (
    <div data-testid="step-summary" className="animate-fade-in">
      {/* Header */}
      <div className="mb-8">
        <p className="font-body text-sm tracking-[0.15em] uppercase text-[#B89F7C] mb-2">
          Étape 5
        </p>
        <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-[#0A0A0A] mb-4">
          Récapitulatif de votre précommande
        </h1>
        <p className="font-body text-[#525252]">
          Vérifiez votre configuration avant de valider votre réservation.
        </p>
      </div>

      {/* Pre-order Status Banner */}
      <div className="bg-[#0A0A0A] text-white p-6 mb-8">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 bg-[#B89F7C]/20 flex items-center justify-center shrink-0">
            <Clock size={20} className="text-[#B89F7C]" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="font-heading text-lg">Statut du projet</h3>
              <span className="bg-[#B89F7C] text-[#0A0A0A] px-3 py-1 text-xs font-medium tracking-wider uppercase">
                Précommande
              </span>
            </div>
            <p className="font-body text-sm text-white/80">
              <span className="font-medium text-[#B89F7C]">Délai estimé de première mise en service : </span>
              entre 1 et 4 mois à compter de la validation du projet.
            </p>
            {hasAcceleratedDeployment && (
              <p className="font-body text-sm text-white/60 mt-2 flex items-center gap-2">
                <Check size={14} className="text-[#B89F7C]" />
                Déploiement accéléré activé — Priorité dans le planning de mise en service.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="space-y-6 mb-8">
        {/* Package */}
        <div className="bg-white border border-[#E5E5E5] p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-[#F9F6F0] flex items-center justify-center">
              <Package size={20} className="text-[#B89F7C]" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <h3 className="font-heading text-lg text-[#0A0A0A] mb-1">
                Offre {pkg.name}
              </h3>
              <p className="font-body text-sm text-[#525252]">
                {pkg.basePrice}€ HT par mois (tarif de base)
              </p>
            </div>
            <div className="text-right">
              <p className="font-heading text-xl text-[#0A0A0A]">{pkg.basePrice}€</p>
              <p className="font-body text-xs text-[#525252]">HT/mois</p>
            </div>
          </div>
        </div>

        {/* Distribution Tier */}
        {tier && (
          <div className="bg-white border border-[#E5E5E5] p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-[#F9F6F0] flex items-center justify-center">
                <TrendingUp size={20} className="text-[#B89F7C]" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <h3 className="font-heading text-lg text-[#0A0A0A] mb-1">
                  Niveau de diffusion : {tier.name}
                </h3>
                <p className="font-body text-sm text-[#525252]">
                  {tier.description}
                </p>
                <p className="font-body text-xs text-[#B89F7C] mt-1">
                  {tier.estimatedExposure} {tier.exposureLabel}
                </p>
              </div>
              {tierPrice > 0 && (
                <div className="text-right">
                  <p className="font-heading text-xl text-[#0A0A0A]">+{tierPrice}€</p>
                  <p className="font-body text-xs text-[#525252]">HT/mois</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Flacons */}
        <div className="bg-white border border-[#E5E5E5] p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-[#F9F6F0] flex items-center justify-center">
              <Droplets size={20} className="text-[#B89F7C]" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <h3 className="font-heading text-lg text-[#0A0A0A] mb-1">
                {pkg.flacons} flacons
              </h3>
              <p className="font-body text-sm text-[#525252]">
                {pkg.mlPerFlacon}ML par flacon / trimestre
              </p>
              <p className="font-body text-xs text-[#B89F7C] mt-1">
                Modification fragrances après {pkg.fragranceModification} jours
              </p>
            </div>
            <div className="text-right">
              <p className="font-body text-sm text-[#525252]">Inclus</p>
            </div>
          </div>
        </div>

        {/* Configuration */}
        <div className="bg-white border border-[#E5E5E5] p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-[#F9F6F0] flex items-center justify-center">
              <Settings size={20} className="text-[#B89F7C]" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <h3 className="font-heading text-lg text-[#0A0A0A] mb-1">
                Configuration
              </h3>
              <p className="font-body text-sm text-[#525252]">
                {numberOfMachines} machine{numberOfMachines > 1 ? "s" : ""}
              </p>
              {machinesCost > 0 && (
                <p className="font-body text-xs text-[#B89F7C] mt-1">
                  +{machinesCost}€ HT/mois ({additionalMachines} machine{additionalMachines > 1 ? "s" : ""} additionnelle{additionalMachines > 1 ? "s" : ""})
                </p>
              )}
            </div>
            {machinesCost > 0 && (
              <div className="text-right">
                <p className="font-heading text-xl text-[#0A0A0A]">+{machinesCost}€</p>
                <p className="font-body text-xs text-[#525252]">HT/mois</p>
              </div>
            )}
          </div>
        </div>

        {/* Add-ons */}
        {selectedAddOns.length > 0 && (
          <div className="bg-white border border-[#E5E5E5] p-6">
            <h3 className="font-heading text-lg text-[#0A0A0A] mb-4">
              Options de préparation activées
            </h3>
            <div className="space-y-3">
              {selectedAddOns.map((addonId) => {
                const addon = PRICING.addOns[addonId];
                return (
                  <div key={addonId} className="flex items-start justify-between">
                    <div className="flex items-start gap-2">
                      <Check size={14} className="text-[#B89F7C] mt-1" />
                      <div>
                        <span className="font-body text-sm text-[#0A0A0A] font-medium">
                          {addon?.name}
                        </span>
                        {addon?.subtitle && (
                          <p className="font-body text-xs text-[#525252]">
                            {addon.subtitle}
                          </p>
                        )}
                      </div>
                    </div>
                    <span className="font-body text-sm text-[#0A0A0A]">
                      +{addon?.price}€ HT/mois
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Logo Customization (VIP only) */}
        {pkg.logoCustomization && (
          <div className="bg-white border border-[#E5E5E5] p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-[#F9F6F0] flex items-center justify-center">
                <Image size={20} className="text-[#B89F7C]" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <h3 className="font-heading text-lg text-[#0A0A0A] mb-1">
                  Personnalisation logo
                </h3>
                {logoFile ? (
                  <div className="flex items-center gap-3 mt-2">
                    <img
                      src={logoFile.url}
                      alt="Logo"
                      className="w-10 h-10 object-contain bg-[#F9F6F0]"
                    />
                    <p className="font-body text-sm text-[#525252]">{logoFile.name}</p>
                  </div>
                ) : (
                  <p className="font-body text-sm text-[#525252]">
                    Aucun logo uploadé
                  </p>
                )}
              </div>
              <div className="text-right">
                <p className="font-body text-sm text-[#B89F7C]">Inclus VIP</p>
              </div>
            </div>
          </div>
        )}

        {/* Engagement */}
        <div className="bg-white border border-[#E5E5E5] p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-[#F9F6F0] flex items-center justify-center">
              <Calendar size={20} className="text-[#B89F7C]" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <h3 className="font-heading text-lg text-[#0A0A0A] mb-1">
                Engagement minimum
              </h3>
              <p className="font-body text-sm text-[#525252]">
                {pkg.engagement} mois à compter de la mise en service effective
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Breakdown */}
      <div className="bg-[#0A0A0A] text-white p-8 mb-8">
        <h3 className="font-body text-sm tracking-[0.15em] uppercase text-[#B89F7C] mb-6">
          Détail des coûts
        </h3>
        <div className="space-y-3 mb-6">
          <div className="flex justify-between font-body">
            <span className="text-white/70">Abonnement de base ({pkg.name})</span>
            <span>{pkg.basePrice}€ HT</span>
          </div>
          {tierPrice > 0 && (
            <div className="flex justify-between font-body">
              <span className="text-white/70">Niveau de diffusion ({tier?.name})</span>
              <span>+{tierPrice}€ HT</span>
            </div>
          )}
          {machinesCost > 0 && (
            <div className="flex justify-between font-body">
              <span className="text-white/70">Machines additionnelles ({additionalMachines})</span>
              <span>+{machinesCost}€ HT</span>
            </div>
          )}
          {addOnsCost > 0 && (
            <div className="flex justify-between font-body">
              <span className="text-white/70">Options de préparation</span>
              <span>+{addOnsCost}€ HT</span>
            </div>
          )}
        </div>
        
        <div className="pt-4 border-t border-white/20 mb-6">
          <div className="flex justify-between items-end">
            <span className="font-body text-white/70">Total mensuel (à compter de la mise en service)</span>
            <span className="font-heading text-4xl">{monthlyPrice}€ <span className="text-lg">HT</span></span>
          </div>
        </div>

        <div className="pt-4 border-t border-white/20">
          <div className="flex justify-between font-body">
            <span className="text-white/70">Frais de réservation et mise en service (unique)</span>
            <span>{setupFee}€ HT</span>
          </div>
        </div>
      </div>

      {/* Total to pay today */}
      <div className="bg-[#F9F6F0] p-6 mb-8">
        <div className="flex justify-between items-center">
          <div>
            <p className="font-body text-sm text-[#525252]">À régler à la validation</p>
            <p className="font-body text-xs text-[#525252] mt-1">
              Frais de réservation et préparation du projet
            </p>
          </div>
          <p className="font-heading text-3xl text-[#0A0A0A]">
            {setupFee}€ <span className="text-lg">HT</span>
          </p>
        </div>
        <div className="mt-4 pt-4 border-t border-[#E5E5E5]">
          <p className="font-body text-xs text-[#525252] flex items-start gap-2">
            <Info size={14} className="text-[#B89F7C] shrink-0 mt-0.5" />
            La facturation récurrente ({monthlyPrice}€ HT/mois) débutera à compter de la mise en service effective du dispositif.
          </p>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-6 border-t border-[#E5E5E5]">
        <button
          onClick={prevStep}
          data-testid="btn-prev-step"
          className="flex items-center gap-2 font-body text-sm text-[#525252] hover:text-[#0A0A0A] transition-colors"
        >
          <ArrowLeft size={16} />
          Retour
        </button>
        <button
          onClick={nextStep}
          data-testid="btn-next-step"
          className="bg-[#0A0A0A] text-white hover:bg-[#B89F7C] transition-colors duration-300 px-8 py-3 text-sm tracking-widest uppercase font-medium flex items-center gap-2"
        >
          Continuer vers la signature
          <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default StepSummary;
